import {
  EntitlementProjectionError,
  fetchEntitlementProjection,
  validateEntitlementChangedEvent,
} from "./entitlementProjection";
import { isOffline, NETWORK_OFFLINE, OFFLINE_MESSAGE } from "./netGuard";

const MAX_EVENT_IDS = 256;
const MAX_INVALIDATION_RETRIES = 1;
const DEFAULT_INVALIDATION_RETRY_DELAY_MS = 250;
const initialState = {
  status: "idle",
  projection: null,
  error: null,
  cause: null,
};

const publicError = (error) => ({
  code: error?.code || "ENTITLEMENT_UNAVAILABLE",
  message:
    error?.message || "Entitlement information is temporarily unavailable.",
  requestId: error?.requestId || null,
  retryable: Boolean(error?.retryable),
});

export const createEntitlementProjectionRepository = ({
  token,
  orgId,
  loadProjection = fetchEntitlementProjection,
  getCachedProjection,
  saveCachedProjection,
  onChange = () => {},
  onError = () => {},
  requestTimeoutMs = 15000,
  invalidationRetryDelayMs = DEFAULT_INVALIDATION_RETRY_DELAY_MS,
} = {}) => {
  let active = true;
  let state = { ...initialState };
  let inFlight = null;
  let requestController = null;
  let cancelRequest = null;
  let queued = false;
  let queuedCause = null;
  let queuedRetryAttempt = 0;
  let retryTimer = null;
  let retryTimerAttempt = 0;
  const timeoutMs =
    Number.isFinite(requestTimeoutMs) && requestTimeoutMs > 0
      ? requestTimeoutMs
      : 15000;
  const retryDelayMs =
    Number.isFinite(invalidationRetryDelayMs) && invalidationRetryDelayMs >= 0
      ? invalidationRetryDelayMs
      : DEFAULT_INVALIDATION_RETRY_DELAY_MS;
  const seenIds = new Set();
  const seenOrder = [];

  const clearRetryTimer = () => {
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
    retryTimerAttempt = 0;
  };

  const scheduleInvalidationRetry = (attempt) => {
    if (!active || attempt >= MAX_INVALIDATION_RETRIES || retryTimer !== null) {
      return;
    }
    retryTimerAttempt = attempt + 1;
    retryTimer = setTimeout(() => {
      const nextAttempt = retryTimerAttempt;
      retryTimer = null;
      retryTimerAttempt = 0;
      if (active) refresh("invalidation", nextAttempt);
    }, retryDelayMs);
  };

  const report = (error) => {
    try {
      Promise.resolve(onError(error)).catch((innerError) => {
        console.warn("entitlementProjectionRepository onError handler failed", innerError);
      });
    } catch (reportError) {
      console.warn("entitlementProjectionRepository report failed", reportError);
    }
  };

  const publish = (next) => {
    if (!active) return;
    state = next;
    try {
      Promise.resolve(onChange(next)).catch(report);
    } catch (error) {
      report(error);
    }
  };

  const hydrateFromCache = () => {
    if (!orgId || typeof getCachedProjection !== "function") return;
    Promise.resolve(getCachedProjection(orgId)).then((cached) => {
      if (!active || !cached || state.projection) return;
      publish({ status: "stale", projection: cached, error: null, cause: "cache" });
    }).catch((error) => {
        report(error instanceof Error ? error : new EntitlementProjectionError({ message: String(error) }));
      });
  };

  if (orgId && typeof getCachedProjection === "function") hydrateFromCache();

  const refresh = (cause = "manual", invalidationRetryAttempt = 0) => {
    if (!active) return Promise.resolve();

    let retryAttempt = Number.isInteger(invalidationRetryAttempt)
      ? Math.max(0, invalidationRetryAttempt)
      : 0;
    if (retryTimer !== null) {
      if (cause === "invalidation") retryAttempt = Math.max(retryAttempt, retryTimerAttempt);
      clearRetryTimer();
    }
    if (inFlight) {
      queued = true;
      if (cause === "invalidation" || queuedCause !== "invalidation") {
        queuedCause = cause;
      }
      queuedRetryAttempt = Math.max(queuedRetryAttempt, retryAttempt);
      return inFlight;
    }

    if (isOffline()) {
      publish({
        status: state.projection ? "stale" : "error",
        projection: state.projection,
        error: {
          code: NETWORK_OFFLINE,
          message: OFFLINE_MESSAGE,
          requestId: null,
          retryable: true,
        },
        cause,
      });
      return Promise.resolve();
    }

    publish({
      status: state.projection ? "stale" : "loading",
      projection: state.projection,
      error: null,
      cause,
    });

    requestController =
      typeof AbortController === "function" ? new AbortController() : null;
    const controller = requestController;
    const load = Promise.resolve().then(() => {
      if (!active) return null;
      return loadProjection({ token, signal: controller?.signal });
    });
    const timedLoad = new Promise((resolve, reject) => {
      let settled = false;
      let timer;
      const settle = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (cancelRequest === cancel) cancelRequest = null;
        callback(value);
      };
      const cancel = () => {
        controller?.abort();
        settle(reject, new EntitlementProjectionError());
      };
      timer = setTimeout(cancel, timeoutMs);
      cancelRequest = cancel;
      load.then(
        (projection) => settle(resolve, projection),
        (error) => settle(reject, error)
      );
    });
    const request = timedLoad
      .then((projection) => {
        publish({ status: "ready", projection, error: null, cause });
        if (orgId && typeof saveCachedProjection === "function") {
          Promise.resolve(saveCachedProjection(orgId, projection)).catch((error) => {
            report(error instanceof Error ? error : new EntitlementProjectionError({ message: String(error) }));
          });
        }
      })
      .catch((error) => {
        if (!active) return;
        report(error);
        const clearProtectedState = [
          "AUTHENTICATION_REQUIRED",
          "FORBIDDEN",
        ].includes(error?.code);
        publish({
          status: clearProtectedState || !state.projection ? "error" : "stale",
          projection: clearProtectedState ? null : state.projection,
          error: publicError(error),
          cause,
        });
        if (
          cause === "invalidation" &&
          error?.retryable &&
          retryAttempt < MAX_INVALIDATION_RETRIES &&
          !queued
        ) {
          scheduleInvalidationRetry(retryAttempt);
        }
      })
      .then(() => {
        if (inFlight !== request) return;
        inFlight = null;
        requestController = null;
        if (!active || !queued) return;
        const nextCause = queuedCause || "coalesced";
        const nextRetryAttempt = queuedRetryAttempt;
        queued = false;
        queuedCause = null;
        queuedRetryAttempt = 0;
        return refresh(nextCause, nextRetryAttempt);
      });
    inFlight = request;
    return request;
  };

  const apply = (candidate) => {
    if (!active) return;
    let event;
    try {
      event = validateEntitlementChangedEvent(candidate);
    } catch (error) {
      report(error);
      return;
    }
    if (seenIds.has(event.eventId)) return;
    seenIds.add(event.eventId);
    seenOrder.push(event.eventId);
    if (seenOrder.length > MAX_EVENT_IDS) {
      seenIds.delete(seenOrder.shift());
    }
    refresh("invalidation");
  };

  const markDisconnected = () => {
    if (!active || state.status === "idle") return;
    publish({
      ...state,
      status: state.projection ? "stale" : "error",
      error:
        state.error ||
        publicError(
          new EntitlementProjectionError({
            message: "Entitlement connection was interrupted. Try again.",
          })
        ),
      cause: "disconnect",
    });
  };

  const cleanup = () => {
    active = false;
    queued = false;
    queuedCause = null;
    queuedRetryAttempt = 0;
    clearRetryTimer();
    cancelRequest?.();
    cancelRequest = null;
    requestController = null;
    seenIds.clear();
    seenOrder.length = 0;
  };

  return { refresh, apply, markDisconnected, cleanup };
};

export default createEntitlementProjectionRepository;
